/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {FocusEvent, Fragment, useEffect, useRef, useState} from "react";
import {Listbox} from "@headlessui/react";
import clsx from "clsx";
import parsePhoneNumber from "libphonenumber-js";
import {countryList, PhoneCountry} from "utils/countryUtils";
import {useTranslation} from "react-i18next";
import {CheckIcon} from "@heroicons/react/24/solid";
import {ExclamationCircleIcon} from "@heroicons/react/24/outline";
import T from "components/T";
import {Overlay} from "react-overlays";
import useGetCountryCode from "utils/hooks/useGetCountryCode";
import FeatherIcon from "components/FeatherIcon";

interface PhoneWithCountrySelectCountryComponentProps {
    countryCode?: string;
    style?: "normal" | "decorated";
}

const PhoneWithCountrySelectCountryComponent = (props: PhoneWithCountrySelectCountryComponentProps) => {
    const [countryFlag, setCountryFlag] = useState(null);

    useEffect(() => {
        const loadCountryFlag = async () => {
            try {
                const countryFlagModule = await import(`components/CountryIcons/3x2/${props.countryCode}.svg`);
                setCountryFlag(countryFlagModule.default);
            } catch (error) {
                console.error(`Error loading country flag for ${props.countryCode}:`, error);
                // Handle error (e.g., display a placeholder flag)
            }
        };

        if (props.countryCode) {
            loadCountryFlag().then(r => {});
        }

        // Clean up on unmount
        return () => {
            setCountryFlag(null);
        };
    }, [props.countryCode]);

    return (
        // <div className={"CountryFlag min-w-[20px] w-[20px] min-h-[13px] h-[13px] border border-gray-200"}>
        <div
            className={clsx(
                "CountryFlag ",
                props?.style === "decorated" &&
                    // "decorated min-w-[45px] w-full min-h-[40px] h-full flex items-center justify-center rounded-tl-[5px] rounded-bl-[5px]",
                    // "decorated min-w-[30px] w-full min-h-[30px] h-full flex items-center justify-center rounded-tl-[5px] rounded-bl-[5px]",
                    "decorated min-w-[38px] md:min-w-[45px] w-full min-h-[32px] md:min-h-[40px] h-full flex items-center justify-center rounded-tl-[5px] rounded-bl-[5px]",
                // !props?.style && "not-decorated min-w-[45px] w-[45px] min-h-[30px] h-[30px] border border-arbo-borderGray"
                // !props?.style && "not-decorated min-w-[20px] w-[20px] min-h-[13px] h-[13px] border border-arbo-borderGray"
                !props?.style && "not-decorated min-w-[45px] w-[45px] min-h-[31px] h-[31px] border border-arbo-borderGray"
            )}
        >
            {countryFlag && (
                <img
                    // className={clsx(props?.style === "decorated" && "object-cover h-full max-w-[45px] w-full rounded-tl-[4px] rounded-bl-[4px]")}
                    // className={clsx(props?.style === "decorated" && "object-cover h-full max-w-[40px] w-full rounded-tl-[4px] rounded-bl-[4px]")}
                    className={clsx(props?.style === "decorated" && "object-cover h-full max-w-[45px] w-full rounded-tl-[4px] rounded-bl-[4px]")}
                    alt={`${props.countryCode} flag`}
                    src={countryFlag}
                />
            )}
        </div>
    );
};

interface ReservationDetailPhoneWithCountrySelectProps {
    options: PhoneCountry[];
    defaultCountry: PhoneCountry;
    value: any;
    register: any;
    name: string;
    label: string;
    className?: string;
    classNameInput?: string;
    required?: boolean;
    errors?: any;
    disabled?: boolean;
    onFocus?: (element: FocusEvent<HTMLInputElement>) => void;
    setValue?: any;
    keyboard?: any;
    setSelectedCountry: (country: PhoneCountry) => void;
    style?: "normal" | "inline";
    driverCountry?: string;
    driverIsInvited?: boolean;
    resetValue?: boolean;
    setResetValue?: (val: boolean) => void;
    setPhoneNumberWasSuggested?: (val: boolean) => void;
}

const ReservationDetailPhoneWithCountrySelect = (props: ReservationDetailPhoneWithCountrySelectProps) => {
    const {t} = useTranslation();
    const getCountryCode = useGetCountryCode();
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry | null>(null);
    const containerRef = useRef(null);
    const targetRef = useRef(null);
    const [show, setShow] = useState(false);
    const options = props?.options;
    const selectedOptionRef = useRef<HTMLLIElement>(null);
    const overlayPlacement = window.innerWidth >= 640 ? "bottom-start" : "top-start";

    const changePhoneNumberToCorrectFormat = async (originPhone: string) => {
        const isDoubleZero = originPhone.startsWith("00", 0);
        const isOneZero = originPhone.startsWith("0", 0);
        if (props?.driverCountry && props?.driverIsInvited) {
            if (isDoubleZero) {
                return "+" + originPhone.slice(2);
            }
            if (isOneZero) {
                const countryCode = await getCountryCode.getCountryCodeFromCountryName(props?.driverCountry);
                const searchedCountryPhonePrefix = countryList.find(item => item.textCode === countryCode);
                return searchedCountryPhonePrefix?.code ? searchedCountryPhonePrefix.code + originPhone.slice(1) : originPhone;
            }
        }
        return originPhone;
    };

    const searchCountryByLanguageCode = (countryCode: string): PhoneCountry => {
        const countryIndex = props.options.findIndex(country => country.textCode == countryCode);
        return props.options[countryIndex];
    };

    const searchCountryByCode = (code: string | null): PhoneCountry | null => {
        if (!code) {
            return null;
        }
        const countryIndex = props.options.findIndex(country => country.textCode === code);
        return props.options[countryIndex];
    };

    const setSelectedCountryHandler = (country: PhoneCountry) => {
        setSelectedCountry(country);
        setShow(false);
        props.setSelectedCountry(country);
        props.setValue(country.code);
    };

    const suggestPhoneNumberCheck = async (currentPhone: string) => {
        const newSuggestPhone = await changePhoneNumberToCorrectFormat(currentPhone);
        if (newSuggestPhone !== undefined) {
            return newSuggestPhone;
        }
        return currentPhone;
    };

    const setPhoneNumber = (newPhone: string) => {
        suggestPhoneNumberCheck(props.value).then(respPhone => {
            const phoneNumber = parsePhoneNumber(respPhone);
            const currentSelectedCountry = searchCountryByCode(phoneNumber?.country ? phoneNumber.country : null);
            if (currentSelectedCountry) {
                setSelectedCountry(currentSelectedCountry);
                props.setSelectedCountry(currentSelectedCountry);
            } else {
                props.setSelectedCountry(props.defaultCountry);
                setSelectedCountry(props.defaultCountry);
            }
            props.setValue(respPhone);
            props?.setPhoneNumberWasSuggested && props.setPhoneNumberWasSuggested(!!(props?.value && respPhone != props.value));
        });
    };

    useEffect(() => {
        if (props?.value) {
            setPhoneNumber(props.value);
        }
        if (!props.value) {
            const defaultCountry = searchCountryByCode(props.defaultCountry?.textCode);
            setSelectedCountry(defaultCountry);
            if (defaultCountry?.code) {
                props.setValue(defaultCountry.code);
            }
        }
    }, [props?.value]);

    // console.log("props.value", props.value);
    useEffect(() => {
        if (props?.resetValue && props?.setResetValue) {
            props?.value ? setPhoneNumber(props.value) : setSelectedCountryHandler(searchCountryByLanguageCode(props.defaultCountry?.textCode));
            props.setResetValue(false);
        }
    }, [props?.resetValue]);

    return (
        <div
            className={clsx(
                "ReservationDetailPhoneWithCountrySelect w-full relative",
                props?.style === "inline" && "sm:flex sm:flex-row sm:space-x-6 sm:items-center sm:flex-wrap",
                props.className
            )}
        >
            <label
                className={clsx(
                    "font-medium mb-1.5 flex text-base md:text-base text-black",
                    // props?.style === "inline" && "sm:!w-1/3 sm:justify-end sm:flex-1",
                    props.errors?.[props.name] ? "text-red-500" : ""
                )}
            >
                <span>{t(props.label)}</span>
                {props.required && <em className={"text-red-500"}>*</em>}
            </label>

            <div
                ref={containerRef}
                className={clsx(
                    "ReservationDetailPhoneWithCountrySelectInner flex flex-row space-x-2 xl:space-x-4 relative flex-1",
                    props?.style === "inline" && "sm:!w-2/3 sm:!flex-none"
                )}
            >
                <Listbox disabled={props.disabled} value={selectedCountry} onChange={setSelectedCountryHandler}>
                    {({open}) => (
                        <div className="w-auto">
                            <Listbox.Button
                                ref={targetRef}
                                className={clsx(
                                    // "relative h-full flex items-center justify-between w-full px-1 py-1 rounded-md border-gray-200 border",
                                    "relative h-full flex items-center justify-between w-full rounded-[5px] border-arbo-borderGray border text-xs md:text-base font-medium",
                                    props.disabled
                                        ? selectedCountry
                                            ? "bg-gray-100 text-gray-600"
                                            : "bg-gray-100 text-gray-400"
                                        : selectedCountry
                                          ? "bg-white text-black"
                                          : "bg-white text-gray-400",
                                    selectedCountry ? "text-black border-arbo-borderGray" : "text-gray-400",
                                    props.errors?.[props.name] && !open && !selectedCountry && "border-red-500"
                                )}
                                onClick={() => setShow(!show)}
                            >
                                <PhoneWithCountrySelectCountryComponent countryCode={selectedCountry?.textCode} style={"decorated"} />
                                {/*<span className="pointer-events-none flex h-full p-[9px] border-l border-arbo-borderGray items-center">*/}
                                <span className="pointer-events-none flex h-full pl-1 pr-1 border-l border-arbo-borderGray items-center">
                                    <FeatherIcon name={"chevron-down"} strokeWidth={2} className={"size-6 flex-none text-black"} />
                                </span>
                            </Listbox.Button>
                            <Overlay placement={overlayPlacement} show={show} target={targetRef.current} rootClose onHide={() => setShow(false)}>
                                {({props, placement}) => (
                                    <Listbox.Options
                                        {...props}
                                        style={{
                                            ...props.style,
                                            transition: "maxHeight 0.3s ease-in-out",
                                            // @ts-ignore
                                            width: containerRef?.current ? containerRef.current.getBoundingClientRect().width : "100%",
                                        }}
                                        className={
                                            // "transition-all duration-300 ease-in h-full drop-shadow shadow-sm bottom-[40px] sm:bottom-auto z-10 max-h-[15rem] w-full overflow-auto rounded-md bg-white py-1 text-base"
                                            "transition-all duration-300 ease-in h-full drop-shadow shadow-sm bottom-[40px] sm:bottom-auto z-10 max-h-[15rem] w-full overflow-auto rounded-[5px] bg-white py-1 text-base"
                                        }
                                        unmount={false}
                                        onLoad={() => {
                                            if (selectedCountry && selectedOptionRef.current) {
                                                selectedOptionRef.current.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "nearest",
                                                });
                                            }
                                        }}
                                    >
                                        {options.map(option => (
                                            <Listbox.Option key={option.textCode} as={Fragment} value={option}>
                                                {({selected, active}) => (
                                                    <li
                                                        ref={selected ? selectedOptionRef : null}
                                                        className={clsx(
                                                            "relative flex flex-row cursor-pointer select-none py-1 pr-0 text-sm",
                                                            active || selected ? "bg-gray-200/80 text-black" : ""
                                                        )}
                                                    >
                                                        {selected && (
                                                            <span className={"w-5 flex items-center"}>
                                                                <CheckIcon className={"w-3.5 h-3.5 ml-0.5"} />
                                                            </span>
                                                        )}
                                                        <div className={clsx("flex items-center truncate", !selected && "pl-5")}>
                                                            <PhoneWithCountrySelectCountryComponent countryCode={option.textCode} />
                                                            <span className={clsx("ml-1 block truncate")}>
                                                                {option.code} {option.name}
                                                            </span>
                                                        </div>
                                                    </li>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                )}
                            </Overlay>
                        </div>
                    )}
                </Listbox>
                <div className={"relative grow flex flex-row"}>
                    <input
                        {...props.register(props.name)}
                        autoComplete={"tel"}
                        className={clsx(
                            // "block w-full px-3 py-1.5 rounded-md border-gray-200 border",
                            // "block w-full px-3 py-1.5 rounded-[5px] border-arbo-borderGray border text-xs md:text-base font-medium",
                            "block w-full px-3 py-2 rounded-[5px] border-arbo-borderGray text-black border text-base md:text-base font-medium",
                            props.classNameInput,
                            props.errors?.[props.name] && "border-red-500",
                            props.disabled ? "bg-gray-100 text-gray-600" : "bg-white"
                        )}
                        defaultValue={props.value}
                        disabled={props.disabled}
                        name={props.name}
                        required={props.required}
                        type={"tel"}
                        onFocus={props.onFocus}
                    />
                </div>
            </div>
            {props.errors?.[props.name]?.message && (
                <div className={clsx("text-red-500 text-sm mt-1 flex flex-row items-center flex-initial")}>
                    <div className={clsx("flex-initial flex flex-row items-center text-xxs md:text-base w-full")}>
                        <ExclamationCircleIcon className={"size-[14px] md:size-[18px] flex-none mr-0.5"} />
                        <T>{props.errors?.[props.name]?.message}</T>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationDetailPhoneWithCountrySelect;
